'use client';

import { Badge, Button, Checkbox, Input, TableCell, TableRow } from '@routly/ui';
import { Trash2 } from 'lucide-react';
import type { SensorDefinition } from '@/lib/types';

interface SensorRowProps {
  name: string;
  definition: SensorDefinition;
  /** Whether this sensor comes from the rung above rather than this one. */
  inherited: boolean;
  onChange: (definition: SensorDefinition) => void;
  onRemove: () => void;
}

/**
 * One sensor, as a row that can be edited in place.
 *
 * The type decides which fields mean anything, so the row shows those and
 * nothing else — a digital sensor has no calibration curve and offering an
 * empty one invites somebody to fill it in.
 */
export function SensorRow({ name, definition, inherited, onChange, onRemove }: SensorRowProps) {
  const sources = 'sources' in definition ? definition.sources.join(', ') : '';

  const setSources = (value: string) =>
    onChange({
      ...definition,
      sources: value
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean),
    } as SensorDefinition);

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium text-foreground">${name}</div>
        {inherited ? (
          <Badge variant="secondary" className="mt-1">
            inherited
          </Badge>
        ) : null}
      </TableCell>

      <TableCell>
        <Badge variant="outline">{definition.type}</Badge>
      </TableCell>

      <TableCell>
        {definition.type === 'virtual' ? (
          <Input
            aria-label={`Expression for ${name}`}
            value={definition.expr}
            onChange={(event) => onChange({ ...definition, expr: event.target.value })}
          />
        ) : (
          <Input
            aria-label={`Sources for ${name}`}
            value={sources}
            placeholder="io_239, din1"
            onChange={(event) => setSources(event.target.value)}
          />
        )}
      </TableCell>

      <TableCell>
        <Details definition={definition} onChange={onChange} name={name} />
      </TableCell>

      <TableCell>
        <Button variant="ghost" size="sm" aria-label={`Remove ${name}`} onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function Details({
  definition,
  onChange,
  name,
}: {
  definition: SensorDefinition;
  onChange: (definition: SensorDefinition) => void;
  name: string;
}) {
  if (definition.type === 'digital') {
    return (
      <div className="flex items-center gap-2">
        <Input
          aria-label={`Bit for ${name}`}
          className="w-20"
          placeholder="bit"
          value={definition.bit ?? ''}
          onChange={(event) =>
            onChange({
              ...definition,
              bit: event.target.value === '' ? undefined : Number(event.target.value),
            })
          }
        />
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          <Checkbox
            checked={definition.invert}
            onChange={(event) => onChange({ ...definition, invert: event.target.checked })}
          />
          invert
        </label>
      </div>
    );
  }

  if (definition.type === 'measurement') {
    return (
      <div className="flex items-center gap-2">
        <Input
          aria-label={`Scale for ${name}`}
          className="w-20"
          value={definition.scale}
          onChange={(event) => onChange({ ...definition, scale: Number(event.target.value) })}
        />
        <Input
          aria-label={`Offset for ${name}`}
          className="w-20"
          value={definition.offset}
          onChange={(event) => onChange({ ...definition, offset: Number(event.target.value) })}
        />
        <span className="text-xs text-muted-foreground">
          {definition.calibration?.length
            ? `${definition.calibration.length}-point curve`
            : 'no curve'}
        </span>
      </div>
    );
  }

  if (definition.type === 'state') {
    return (
      <span className="text-xs text-muted-foreground">
        {Object.entries(definition.states)
          .map(([code, label]) => `${code}→${label}`)
          .join(', ') || 'no codes'}
      </span>
    );
  }

  return <span className="text-xs text-muted-foreground">—</span>;
}
